import React, { useState, useEffect } from "react";
import { Layout, Row, Col, Card, Typography, Spin, Select, Input, DatePicker, Button, Empty, Alert, message, Form, Space, Tag, Tabs, Divider, Tooltip } from "antd";
import { FilterOutlined, ReloadOutlined, SearchOutlined, InfoCircleOutlined, CheckCircleOutlined, HistoryOutlined, BellOutlined, SyncOutlined } from "@ant-design/icons";
import { getActionItems } from "../../../api/actionItemApi";
import { flightOperationApi } from "../../../api/flightOperationFlightApi";
import { airportApi } from "../../../api/masterDataApi";
import NotificationCard from "../../../components/common/NotificationCard";
import NotificationDetailView from "../../../components/common/NotificationDetailView";
import { ProcessingStage, ResolutionStatus, ActionItemStatus, ActionItemResolution, HISTORY_STATUS_FILTER_OPTIONS, SOURCE_TYPE_FILTER_OPTIONS, ACTION_TYPE_FILTER_OPTIONS, PRIORITY_FILTER_OPTIONS } from "./constants/actionItemConstants";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

/**
 * 待办通知处理中心页面
 * 展示和处理系统生成的行动项
 */
const NotificationCenterPage = () => {
    // 状态管理
    const [loading, setLoading] = useState(false);
    const [actionItems, setActionItems] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 100 });
    const [selectedItem, setSelectedItem] = useState(null);
    const [currentFlightData, setCurrentFlightData] = useState(null);
    const [flightDataLoading, setFlightDataLoading] = useState(false);
    const [filterForm] = Form.useForm();
    const [hasError, setHasError] = useState(false);
    const [activeTab, setActiveTab] = useState("pending"); // 'pending' 待处理, 'history' 历史
    const [airportsMap, setAirportsMap] = useState({});

    // 获取机场数据
    const fetchAirportsData = async () => {
        try {
            const response = await airportApi.getAirportList({ page_size: 500 });
            const airports = response.data.items || [];

            // 转换为以机场代码为键的对象
            const airportsObj = {};
            airports.forEach((airport) => {
                if (airport.iata_code) {
                    airportsObj[airport.iata_code] = airport;
                }
            });

            setAirportsMap(airportsObj);
        } catch (error) {
            console.error("获取机场数据失败:", error);
            message.error("获取机场数据失败，时区显示可能不准确");
        }
    };

    // 获取行动项列表
    const fetchActionItems = async (filters = {}) => {
        setLoading(true);
        setHasError(false);

        try {
            // V3.0 更新：根据当前选择的标签页确定查询参数
            const params = {
                page: pagination.page,
                page_size: pagination.page_size,
                sort_by: "created_at",
                order: "desc",
                ...filters,
            };

            if (activeTab === "pending") {
                // 待处理：使用后端特殊聚合状态
                params.status = "pending_manual_review";
            } else {
                // 历史：查询已完成的状态
                // 使用新的状态值，后端会在 status 和 resolution 字段中查找
                const historyStatuses = [ActionItemStatus.COMPLETED, ActionItemStatus.FAILED, ActionItemStatus.REJECTED];
                const historyResolutions = [ActionItemResolution.AUTO_EXECUTED, ActionItemResolution.USER_CONFIRMED, ActionItemResolution.USER_MODIFIED, ActionItemResolution.USER_REJECTED];
                // 合并状态和解决状态，后端会在两个字段中查找
                params.status = [...historyStatuses, ...historyResolutions].join(",");
            }

            const response = await getActionItems(params);
            setActionItems(response.data.items || []);
            setPagination({
                ...pagination,
                total: response.data.total || 0,
                page: params.page,
            });

            // 如果有结果并且当前没有选中项，默认选中第一项
            if (response.data.items && response.data.items.length > 0 && !selectedItem) {
                handleSelectItem(response.data.items[0]);
            } else if (response.data.items.length === 0) {
                setSelectedItem(null);
                setCurrentFlightData(null);
            }
        } catch (error) {
            console.error("获取行动项列表失败:", error);
            message.error(activeTab === "pending" ? "获取待处理通知失败，请稍后重试" : "获取历史通知失败，请稍后重试");
            setHasError(true);
            setActionItems([]);
            setSelectedItem(null);
        } finally {
            setLoading(false);
        }
    };

    // 标签页变更时刷新数据
    const handleTabChange = (key) => {
        setActiveTab(key);
        setSelectedItem(null);
        setCurrentFlightData(null);
        setPagination({ ...pagination, page: 1 });
        filterForm.resetFields();
    };

    // 初始加载
    useEffect(() => {
        fetchActionItems();
        fetchAirportsData(); // 加载机场数据
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 监听activeTab变化，当tab切换时重新获取数据
    useEffect(() => {
        fetchActionItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // 处理选择行动项
    const handleSelectItem = async (item) => {
        setSelectedItem(item);
        setCurrentFlightData(null);

        // 获取flight_operation_id，优先从顶层获取，如果为null则从system_operations中获取
        let flightOperationId = item?.flight_operation_id;

        if (!flightOperationId && item?.suggested_payload?.system_operations?.length > 0) {
            // 从第一个system_operation中获取flight_operation_id
            const firstOperation = item.suggested_payload.system_operations[0];
            flightOperationId = firstOperation?.parameters?.flight_operation_id;
        }

        if (flightOperationId) {
            setFlightDataLoading(true);
            try {
                const response = await flightOperationApi.getFlightOperationById(flightOperationId);
                // 正确提取 response.data 中的航班数据
                setCurrentFlightData(response.data);
            } catch (error) {
                console.error(`获取航班ID ${flightOperationId} 的运营数据失败:`, error);
                message.error("获取关联航班数据失败，部分信息可能不准确");
                setCurrentFlightData(null);
            }
            setFlightDataLoading(false);
        } else {
            setCurrentFlightData(null);
        }
    };

    // 处理搜索/过滤
    const handleSearch = () => {
        const values = filterForm.getFieldsValue();
        const filters = {};

        if (values.search_key) filters.search_key = values.search_key;
        if (values.date_range && values.date_range.length === 2) {
            filters.created_after = values.date_range[0].startOf("day").toISOString();
            filters.created_before = values.date_range[1].endOf("day").toISOString();
        }
        if (values.status && activeTab === "history") filters.status = values.status;

        setPagination({ ...pagination, page: 1 }); // 重置到第一页
        fetchActionItems(filters);
    };

    // 重置筛选条件
    const handleReset = () => {
        filterForm.resetFields();
        setPagination({ ...pagination, page: 1 });
        fetchActionItems();
    };

    // 处理行动项处理完成后的刷新
    const handleProcessed = () => {
        fetchActionItems();
        setSelectedItem(null);
    };

    // 获取历史通知状态下拉选项
    const getHistoryStatusOptions = () => {
        return HISTORY_STATUS_FILTER_OPTIONS.map((groupOrOption) => {
            if (groupOrOption.options) {
                // This is an OptGroup
                return (
                    <Select.OptGroup label={groupOrOption.label} key={groupOrOption.label}>
                        {groupOrOption.options.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select.OptGroup>
                );
            } else {
                // This is a direct Option
                return (
                    <Select.Option key={groupOrOption.value} value={groupOrOption.value}>
                        {groupOrOption.label}
                    </Select.Option>
                );
            }
        });
    };

    return (
        <div className="page-container">
            {/* 页面头部 */}
            <div className="page-header-compact">
                <div className="page-title-compact">待办通知处理中心</div>
                <div className="page-action-compact">
                    <Space size="middle" wrap>
                        {/* 标签页切换 */}
                        <Tabs activeKey={activeTab} onChange={handleTabChange} size="small">
                            <TabPane
                                tab={
                                    <span>
                                        <BellOutlined /> 待处理通知
                                    </span>
                                }
                                key="pending"
                            />
                            <TabPane
                                tab={
                                    <span>
                                        <HistoryOutlined /> 历史通知
                                    </span>
                                }
                                key="history"
                            />
                        </Tabs>

                        <Divider type="vertical" />

                        {/* 操作按钮 */}
                        <Tooltip title="刷新数据">
                            <Button icon={<SyncOutlined spin={loading} />} onClick={() => fetchActionItems()} loading={loading}>
                                刷新
                            </Button>
                        </Tooltip>
                    </Space>
                </div>
            </div>

            {/* 筛选区域 */}
            <Card className="notification-filter-card" style={{ marginBottom: "16px" }}>
                <Form form={filterForm} layout="vertical" onFinish={handleSearch}>
                    <Row gutter={16} align="bottom">
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item name="search_key" label="搜索关键字">
                                <Input prefix={<SearchOutlined />} placeholder="航班号/机场/内容" allowClear />
                            </Form.Item>
                        </Col>

                        {activeTab === "history" ? (
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item name="status" label="处理状态">
                                    <Select placeholder="选择处理状态" allowClear>
                                        {getHistoryStatusOptions()}
                                    </Select>
                                </Form.Item>
                            </Col>
                        ) : null}

                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item name="date_range" label="创建时间">
                                <RangePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={24} lg={activeTab === "history" ? 6 : 12} style={{ textAlign: "right" }}>
                            <Form.Item>
                                <Space>
                                    <Button onClick={handleReset} icon={<ReloadOutlined />}>
                                        重置
                                    </Button>
                                    <Button type="primary" htmlType="submit" icon={<FilterOutlined />} loading={loading}>
                                        应用筛选
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* 主内容区 */}
            <Row gutter={16}>
                {/* 左侧通知列表 */}
                <Col span={8}>
                    <Card
                        title={
                            <Space>
                                <span>{activeTab === "pending" ? "待处理通知" : "历史通知"}</span>
                                <Tag color="blue">{pagination.total || 0}条</Tag>
                            </Space>
                        }
                        style={{ minHeight: "600px", height: "calc(100vh - 80px)", overflowY: "auto" }}
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: "100px 0" }}>
                                <Spin />
                                <div style={{ marginTop: "12px", color: "#8c8c8c" }}>加载中...</div>
                            </div>
                        ) : hasError ? (
                            <Alert message="加载失败" description="获取通知列表失败，请刷新页面重试" type="error" showIcon />
                        ) : actionItems.length === 0 ? (
                            <Empty description={activeTab === "pending" ? "暂无待处理通知" : "暂无历史通知"} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "100px 0" }} />
                        ) : (
                            <div>
                                {actionItems.map((item) => (
                                    <NotificationCard key={item.id} actionItem={item} onClick={handleSelectItem} selected={selectedItem && selectedItem.id === item.id} />
                                ))}
                            </div>
                        )}
                    </Card>
                </Col>

                {/* 右侧详情与处理区 */}
                <Col span={16}>
                    <div style={{ minHeight: "600px", height: "calc(100vh - 80px)", overflowY: "auto" }}>
                        <NotificationDetailView actionItem={selectedItem} currentFlightData={currentFlightData} onProcessed={handleProcessed} flightDataLoading={flightDataLoading} airportsMap={airportsMap} />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default NotificationCenterPage;
